import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Zap, Play, Pause, Settings, Trash2 } from 'lucide-react';
import { mockAutomations } from '@/lib/mock-data';

export default function AutomationsPage() {
  const [automations, setAutomations] = useState(mockAutomations);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const toggleAutomation = (id) => {
    setAutomations((prev) =>
      prev.map((a) =>
        a.id === id
          ? { ...a, status: a.status === 'active' ? 'paused' : 'active' }
          : a
      )
    );
  };

  const activeCount = automations.filter((a) => a.status === 'active').length;
  const totalRuns = automations.reduce((sum, a) => sum + a.runs, 0);

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Automations</h1>
          <p className="text-muted-foreground">Automate your WhatsApp workflows</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-automation-button" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Automation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Automation</DialogTitle>
              <DialogDescription>
                Set up an automated workflow for your WhatsApp business.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Automation Name</Label>
                <Input id="name" placeholder="e.g., Welcome Message" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trigger">Trigger</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_customer">New customer</SelectItem>
                    <SelectItem value="order_placed">Order placed</SelectItem>
                    <SelectItem value="order_delivered">Order delivered</SelectItem>
                    <SelectItem value="cart_abandoned">Cart abandoned</SelectItem>
                    <SelectItem value="low_stock">Low stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="action">Action</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="send_message">Send WhatsApp message</SelectItem>
                    <SelectItem value="send_template">Send template message</SelectItem>
                    <SelectItem value="create_task">Create task</SelectItem>
                    <SelectItem value="notify_team">Notify team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setIsDialogOpen(false)}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Automations</p>
            <p className="text-2xl font-bold">{automations.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Runs</p>
            <p className="text-2xl font-bold">{totalRuns.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Automations List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {automations.map((automation) => (
          <Card key={automation.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    automation.status === 'active' 
                      ? 'bg-green-100 dark:bg-green-900/30' 
                      : 'bg-muted'
                  }`}>
                    <Zap className={`w-5 h-5 ${
                      automation.status === 'active' 
                        ? 'text-green-600' 
                        : 'text-muted-foreground'
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{automation.name}</CardTitle>
                    <CardDescription className="text-xs">
                      Trigger: {automation.trigger}
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={automation.status === 'active'}
                  onCheckedChange={() => toggleAutomation(automation.id)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <Badge variant="secondary">
                    {automation.runs.toLocaleString()} runs
                  </Badge>
                  <Badge variant={automation.status === 'active' ? 'default' : 'secondary'}>
                    {automation.status === 'active' ? (
                      <><Play className="w-3 h-3 mr-1" /> Active</>
                    ) : (
                      <><Pause className="w-3 h-3 mr-1" /> Paused</>
                    )}
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon">
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
